import { ContentLayout, ContentMain } from 'components/ContentLayout'
import ListHeader from 'components/ListHeader'
import React from 'react'
import { observer } from 'mobx-react'
import styles from './TeamMembers.m.sass'
import TeamMembersViewModel from './TeamMembersViewModel'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import SearchBar from './SearchBar'
import MembersTable from './MembersTable'
import PendingMembersTable from './PendingMembersTable'
import ConfirmDialog from 'components/ConfirmDialog'
import InviteTeamMemberModal from 'iam/components/InviteTeamMemberModal'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import { match } from 'utils/pattern-match-util'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  vm: TeamMembersViewModel
}

export default observer(function TeamMembers({ vm }: Props) {
  return (
    <ContentLayout accountForLabel accountForTabBar>
      <ContentMain>
        {vm.activate.match({
          pending: () => <ContentLoading />,
          rejected: (err: any) => <ContentError error={err} />,
          resolved: () => (
            <div className={styles.root}>
              <div className={styles.headerContainer}>
                <MembersListHeader vm={vm} />
              </div>
              <div className={styles.box}>
                <div className={styles.actions}>
                  <SearchBar vm={vm} />
                </div>
                <div className={styles.table}>
                  {match(vm.visibleKind, {
                    Current: () => (
                      <MembersTable vm={vm} teamMembers={vm.teamMembers} />
                    ),
                    Pending: () => (
                      <PendingMembersTable vm={vm.pendingMembersVM} />
                    ),
                  })}
                </div>
              </div>
            </div>
          ),
        })}
      </ContentMain>

      {vm.inviteTeamMemberModalVM && (
        <InviteTeamMemberModal vm={vm.inviteTeamMemberModalVM} />
      )}
      <ConfirmDialog
        title="Confirm removal"
        yesText="Remove Team Member"
        danger
        state={vm.removeConfirmDialog}
      >
        {/* removeTeamMember is a task, so just use whatever team member it was called with */}
        {vm.removeTeamMember.args && vm.removeTeamMember.args[0] && (
          <span>
            Are you sure you want to remove{' '}
            <strong>{vm.removeTeamMember.args[0].member.displayName}</strong>?
            You can invite them back again later.
          </span>
        )}
      </ConfirmDialog>
    </ContentLayout>
  )
})

interface MemberListHeaderProps {
  vm: TeamMembersViewModel
}

const MembersListHeader = observer(({ vm }: MemberListHeaderProps) => {
  const isDropdown = vm.hasPendingMembers && vm.canManageTeamMember

  if (!isDropdown) {
    return <MemberListHeaderLabel vm={vm} />
  }

  return (
    <Dropdown
      autoDismiss={true}
      button={(buttonProps, ref) => (
        <MemberListHeaderLabel
          vm={vm}
          innerRef={ref}
          onClick={buttonProps.onClick}
          icon={<Icon name="arrow" rotate={90} />}
        />
      )}
    >
      <DropdownTitle>I want to view...</DropdownTitle>
      <DropdownItem
        active={vm.visibleKind === 'Current'}
        checkmark={vm.visibleKind === 'Current'}
        onClick={() => vm.setVisibleKind('Current')}
      >
        Current Members
      </DropdownItem>
      <DropdownItem
        active={vm.visibleKind === 'Pending'}
        checkmark={vm.visibleKind === 'Pending'}
        onClick={() => vm.setVisibleKind('Pending')}
      >
        Pending Acceptance
      </DropdownItem>
    </Dropdown>
  )
})

interface MemberListHeaderLabelProps {
  vm: TeamMembersViewModel
  onClick?: () => unknown
  innerRef?: React.LegacyRef<HTMLElement | null>
  icon?: React.ReactNode
}

const MemberListHeaderLabel = observer(
  ({ vm, onClick, icon, innerRef }: MemberListHeaderLabelProps) => {
    return match(vm.visibleKind, {
      Current: () => (
        <ListHeader
          onClick={onClick}
          iconRight={icon}
          count={vm.teamMembers.length}
          labelRef={innerRef}
        >
          Team Members
        </ListHeader>
      ),
      Pending: () => (
        <ListHeader
          onClick={onClick}
          count={vm.pendingTeamMembers.length}
          iconRight={icon}
          labelRef={innerRef}
        >
          Pending Members
        </ListHeader>
      ),
    })
  }
)
